import { Box, Button, Group, Paper, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useUserContext } from "@/context/UserContext";
import { sidebarTourStorageKey } from "@/shared/constants/sidebarTour.const";

const STEPS = [
  {
    id: "profile",
    title: "My Profile",
    body: "View and manage your company profile, verification status, and how others see you on the platform.",
  },
  {
    id: "my-discovery",
    title: "Explore Companies",
    body: "Discover companies that fit your goals and learn more before you reach out.",
  },
  {
    id: "shortlist",
    title: "Shortlist",
    body: "Save companies you want to revisit or contact later so you do not lose track of good fits.",
  },
  {
    id: "dashboard",
    title: "Dashboard",
    body: "Manage collaboration requests you have sent and received, including pending counts at a glance.",
  },
  {
    id: "collaborations",
    title: "Collaborations",
    body: "Open active collaborations, shared workspace, and matched opportunities in one place.",
  },
  {
    id: "usage-guide-bot",
    title: "Your helping hand",
    body: "Tap this guide anytime for a step-by-step walkthrough of how to use GTsetu, your on-demand helping hand while you explore the platform.",
  },
] as const;

type StepId = (typeof STEPS)[number]["id"];

function queryTarget(id: StepId): Element | null {
  return document.querySelector(`[data-sidebar-tour="${id}"]`);
}

function readRect(el: Element | null): DOMRect | null {
  if (!el) return null;
  return el.getBoundingClientRect();
}

export function SidebarOnboardingTour() {
  const { userDetails } = useUserContext();
  const isDesktop = useMediaQuery("(min-width: 48em)", false, { getInitialValueInEffect: true });
  const companyStatus = userDetails?.company?.status;
  const isSystemVerified = companyStatus === "SYSTEM_VERIFIED" || companyStatus === "VERIFIED";
  const userId = userDetails?.id;

  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const dismissTour = useCallback(
    (markComplete: boolean) => {
      if (markComplete && userId) {
        try {
          localStorage.setItem(sidebarTourStorageKey(userId), "1");
        } catch {
          /* ignore quota / private mode */
        }
      }
      setStepIndex(null);
      setRect(null);
    },
    [userId],
  );

  useEffect(() => {
    if (!userDetails) {
      setStepIndex(null);
      setRect(null);
    }
  }, [userDetails]);

  useEffect(() => {
    if (!userId || !isSystemVerified || isDesktop !== true) return;
    try {
      if (localStorage.getItem(sidebarTourStorageKey(userId))) return;
    } catch {
      return;
    }
    const t = window.setTimeout(() => setStepIndex(0), 450);
    return () => window.clearTimeout(t);
  }, [userId, isSystemVerified, isDesktop]);

  const updateRect = useCallback(() => {
    if (stepIndex === null) {
      setRect(null);
      return;
    }
    const id = STEPS[stepIndex]?.id;
    if (!id) {
      setRect(null);
      return;
    }
    setRect(readRect(queryTarget(id)));
  }, [stepIndex]);

  useLayoutEffect(() => {
    updateRect();
  }, [updateRect, stepIndex]);

  useEffect(() => {
    if (stepIndex === null) return;
    const onWin = () => updateRect();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    const t = window.setInterval(updateRect, 400);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
      window.clearInterval(t);
    };
  }, [stepIndex, updateRect]);

  useEffect(() => {
    if (stepIndex === null) return;
    const id = STEPS[stepIndex]?.id;
    if (!id) return;
    const fail = window.setTimeout(() => {
      if (!queryTarget(id)) dismissTour(false);
    }, 10000);
    return () => window.clearTimeout(fail);
  }, [stepIndex, dismissTour]);

  if (stepIndex === null || !rect || rect.width === 0) {
    return null;
  }

  const step = STEPS[stepIndex];
  if (!step) return null;

  const pad = 8;
  const top = rect.top - pad;
  const left = rect.left - pad;
  const width = rect.width + pad * 2;
  const height = rect.height + pad * 2;
  const isLast = stepIndex === STEPS.length - 1;

  const cardW = 320;
  const gap = 16;
  const rightEdge = left + width;
  const canPlaceRight = rightEdge + gap + cardW <= window.innerWidth - gap;
  const canPlaceLeft = left - gap - cardW >= gap;
  const cardLeftPos = canPlaceRight
    ? rightEdge + gap
    : canPlaceLeft
      ? left - gap - cardW
      : Math.min(Math.max(gap, left), window.innerWidth - cardW - gap);
  const cardTopPos = Math.min(Math.max(gap, top), window.innerHeight - 240);

  return (
    <Box pos="fixed" inset={0} style={{ zIndex: 10050 }} data-sidebar-tour-root>
      <Box
        pos="fixed"
        style={{
          top,
          left,
          width,
          height,
          borderRadius: 10,
          boxShadow: "0 0 0 3px #ffc300, 0 0 0 9999px rgba(15, 23, 42, 0.74)",
          pointerEvents: "none",
          zIndex: 10050,
        }}
      />
      <Box
        pos="fixed"
        inset={0}
        style={{ zIndex: 10051, cursor: "default" }}
        onMouseDown={(e) => e.preventDefault()}
      />
      <Paper
        shadow="md"
        p="md"
        radius="md"
        pos="fixed"
        maw={cardW}
        w={cardW}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          top: cardTopPos,
          left: cardLeftPos,
          zIndex: 10052,
          border: "1px solid rgba(148, 163, 184, 0.35)",
        }}
      >
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={6}>
          Quick tour · {stepIndex + 1} of {STEPS.length}
        </Text>
        <Text fw={700} size="lg" mb="xs">
          {step.title}
        </Text>
        <Text size="sm" c="dimmed" mb="lg" style={{ lineHeight: 1.55 }}>
          {step.body}
        </Text>
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Button variant="subtle" size="compact-sm" color="gray" onClick={() => dismissTour(true)}>
            Skip
          </Button>
          <Group gap="xs" wrap="nowrap">
            {stepIndex > 0 && (
              <Button variant="default" size="compact-sm" onClick={() => setStepIndex((s) => (s !== null ? s - 1 : s))}>
                Back
              </Button>
            )}
            <Button
              size="compact-sm"
              style={{ backgroundColor: "#ffc300", color: "var(--mantine-color-primary-7)" }}
              onClick={() => {
                if (isLast) dismissTour(true);
                else setStepIndex((s) => (s !== null ? s + 1 : s));
              }}
            >
              {isLast ? "Done" : "Next"}
            </Button>
          </Group>
        </Group>
      </Paper>
    </Box>
  );
}
